import type { ChartLayout } from "../types.js";
import { ensureAn26DesignStyles } from "./injectStyles.js";
import type { HorizontalBarChartPayload } from "../normalize.js";
import { mountHorizontalBarChart, seriesColors } from "./horizontalBarChartCore.js";

function esc(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function fmtCell(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n) || n === 0) return "-";
  return n.toFixed(2);
}

function buildTableHtml(data: HorizontalBarChartPayload): string {
  const series = data.series.slice(0, 2);
  const colHeads = series
    .map((s) => `<th class="an26-table--split-hbar__val">${esc(s.name)}</th>`)
    .join("");
  const rows = data.categories
    .map((cat, ci) => {
      const cells = series
        .map((s) => `<td class="an26-table--split-hbar__val">${esc(fmtCell(s.data[ci]))}</td>`)
        .join("");
      return `<tr><td class="an26-table__label">${esc(cat)}</td>${cells}</tr>`;
    })
    .join("");
  return `<div class="an26-table-wrap"><table class="an26-table an26-table--split-hbar" role="grid">
    <thead><tr><th></th>${colHeads}</tr></thead>
    <tbody>${rows}</tbody>
  </table></div>`;
}

export interface SplitHorizontalBarChartRenderOptions {
  title?: string | null;
  subtitle?: string | null;
  layout?: ChartLayout;
}

export function renderSplitHorizontalBarChart(
  container: HTMLElement,
  data: HorizontalBarChartPayload,
  opts?: SplitHorizontalBarChartRenderOptions
): () => void {
  ensureAn26DesignStyles();
  const embedded = opts?.layout === "embedded";
  const title = opts?.title ?? "Sector Allocation";
  const subtitle =
    opts?.subtitle ??
    (data.subtitle || (data.asOf ? `As of ${data.asOf} % of Total (Updated Quarterly)` : ""));
  const chartHostId = `an26-split-hbar-hc-${Math.random().toString(36).slice(2, 9)}`;
  const head = embedded
    ? ""
    : `<header class="an26-root__head">
        <h2 class="an26-root__title">${esc(String(title))}</h2>
        ${subtitle ? `<p class="an26-root__subtitle">${esc(subtitle)}</p>` : ""}
      </header>`;
  const rootClass = embedded
    ? "an26-root an26-root--split-hbar an26-root--embedded"
    : "an26-root an26-root--split-hbar";

  const legendItems = data.series
    .map((s, i) => {
      const { baseColor } = seriesColors(s.name, i);
      return `<span class="an26-legend__item"><span class="an26-legend__sw" style="background:${esc(baseColor)}"></span>${esc(s.name)}</span>`;
    })
    .join("");

  container.innerHTML = `
    <div class="${rootClass}">
      ${head}
      <div class="an26-split an26-split--split-hbar">
        <div class="an26-split-hbar__table">${buildTableHtml(data)}</div>
        <div class="an26-split-hbar__chart">
          <div class="an26-root__chart-wrap">
            <div id="${chartHostId}" class="an26-root__chart"></div>
          </div>
          <div class="an26-legend" role="list">${legendItems}</div>
        </div>
      </div>
    </div>`;

  const mount = container.querySelector(`#${chartHostId}`) as HTMLElement | null;
  if (!mount || !data.categories.length || !data.series.length) {
    return () => container.replaceChildren();
  }

  const destroyChart = mountHorizontalBarChart(mount, data, { esc });

  return () => {
    destroyChart();
    container.replaceChildren();
  };
}
